import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { categories } from '../mock';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Grid, List } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Products = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProducts();
  }, [selectedCategory]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const url = selectedCategory === 'all' 
        ? `${API}/products`
        : `${API}/products?category=${selectedCategory}`;
      
      const response = await axios.get(url);
      setProducts(response.data.products);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-12">
          <h1 className="font-display text-4xl md:text-5xl font-bold text-[#0a1128] mb-4">
            Our Collection
          </h1>
          <p className="text-xl text-gray-600">
            Premium hookahs, tobacco, and accessories for the discerning enthusiast
          </p>
        </div>

        {/* Filters */}
        <div className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <Button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                variant={selectedCategory === category.id ? 'default' : 'outline'}
                className={selectedCategory === category.id
                  ? 'bg-[#D4AF37] hover:bg-[#C5A028] text-[#0a1128]'
                  : 'border-gray-300 hover:border-[#D4AF37]'
                }
              >
                {category.name}
              </Button>
            ))}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => setViewMode('grid')}
              className={viewMode === 'grid' ? 'border-[#D4AF37] text-[#D4AF37]' : ''}
            >
              <Grid className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setViewMode('list')}
              className={viewMode === 'list' ? 'border-[#D4AF37] text-[#D4AF37]' : ''}
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Products Grid */}
        <div className={viewMode === 'grid'
          ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8'
          : 'space-y-6'
        }>
          {loading ? (
            <div className="col-span-full text-center py-12">
              <p className="text-gray-600">Loading products...</p>
            </div>
          ) : products.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <p className="text-gray-600">No products found</p>
            </div>
          ) : (
            products.map((product) => (
              <Card key={product.id} className="product-card border-gray-200 overflow-hidden h-full flex flex-col">
                <div className={viewMode === 'grid' ? 'aspect-square' : 'flex'}>
                  <img
                    src={product.image}
                    alt={product.name}
                    className={viewMode === 'grid'
                      ? 'w-full h-full object-cover transition-transform duration-300 hover:scale-110'
                      : 'w-48 h-48 object-cover'
                    }
                  />
                  <CardContent className={viewMode === 'grid' ? 'p-6 flex flex-col' : 'flex-1 p-6 flex flex-col'}>
                    <div className="flex items-start justify-between mb-2">
                      <p className="text-xs font-medium text-[#D4AF37] uppercase tracking-wider">
                        {product.category}
                      </p>
                      {product.stock < 10 && (
                        <span className="text-xs text-red-600 font-medium whitespace-nowrap ml-2">
                          Only {product.stock} left
                        </span>
                      )}
                    </div>
                    <h3 className="text-lg font-semibold text-[#0a1128] mb-2 line-clamp-1">
                      {product.name}
                    </h3>
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2 flex-1">
                      {product.description}
                    </p>
                    <div className="flex items-center justify-between mt-auto gap-3">
                      <span className="text-xs font-medium text-[#D4AF37] uppercase tracking-wider">
                        Premium Collection
                      </span>
                      <Button
                        onClick={() => navigate('/contact')}
                        className="bg-[#D4AF37] hover:bg-[#C5A028] text-[#0a1128] whitespace-nowrap"
                        data-testid={`quote-btn-${product.id}`}
                        disabled={product.stock === 0}
                      >
                        {product.stock === 0 ? 'Unavailable' : 'Get a Quote'}
                      </Button>
                    </div>
                  </CardContent>
                </div>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Products;